import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '@env/environment';
import { LoginRequest, TokenResponse, UsuarioAutenticado } from '@core/auth/auth.models';

const STORAGE_KEY = 'gdic.accessToken';

/**
 * Espelha a RoleHierarchy do backend (SecurityConfig): cada papel mapeia os
 * papéis que ele contém.
 *
 * É preciso repetir isso aqui porque o JWT carrega apenas as authorities
 * literais do usuário — a hierarquia só é aplicada no servidor. Sem este mapa,
 * um ESIC_GESTOR pareceria não ter ESIC_CADASTRADOR e a interface esconderia
 * ações que o backend lhe permite. Mantenha em sincronia com o bean
 * roleHierarchy() do backend.
 */
const HIERARQUIA_DE_PAPEIS: Readonly<Record<string, readonly string[]>> = {
  ESIC_GESTOR: ['ESIC_CADASTRADOR', 'ESIC_RESPONDENTE'],
  ESIC_CADASTRADOR: ['ESIC_RESPONDENTE'],
  OUVIDORIA_ADMIN: ['OUVIDORIA_CADASTRADOR', 'OUVIDORIA_OPERADOR'],
  OUVIDORIA_CADASTRADOR: ['OUVIDORIA_OPERADOR'],
  GDIP_ADMIN: ['GDIP_OPERADOR'],
};

/** Margem (em segundos) para considerar o token expirado antes do exp real. */
const CLOCK_SKEW_S = 30;

/**
 * Autenticação contra o gdic-api (POST /api/v1/auth/login).
 *
 * Guarda o access token JWT em localStorage e expõe o estado da sessão como
 * signals somente-leitura. O header Authorization é adicionado pelas demais
 * requisições via authInterceptorFn (auth.interceptor.ts).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly http = inject(HttpClient);
  private readonly loginUrl = `${environment.backend}/api/v1/auth/login`;

  private readonly accessToken = signal<string | null>(restaurarToken());

  /** Claims do usuário logado, ou null se não autenticado. */
  readonly usuario = computed<UsuarioAutenticado | null>(() => {
    const token = this.accessToken();
    return token ? decodificarClaims(token) : null;
  });

  readonly isAuthenticated = computed(() => this.usuario() !== null);

  /** Authorities do token (ROLE_<GRUPO>_<PAPEL>). */
  readonly roles = computed<string[]>(() => this.usuario()?.roles ?? []);

  /**
   * Autentica no backend e, em caso de sucesso, persiste o token.
   * Erros chegam ao subscriber como HttpErrorResponse cujo error é um
   * ProblemDetail RFC-7807 ({ code, detail, traceId }): 401 credencial
   * inválida, 403 usuário sem vínculo com o tenant.
   */
  login(credenciais: LoginRequest): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(this.loginUrl, credenciais).pipe(
      tap(({ accessToken }) => {
        localStorage.setItem(STORAGE_KEY, accessToken);
        this.accessToken.set(accessToken);
      })
    );
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.accessToken.set(null);
  }

  /**
   * Substitui o token vigente por um reemitido pelo backend — hoje, a troca de e-mail de login
   * (POST /api/v1/conta/email), que muda o claim `email`. Sem isso a sessão seguiria com o
   * claim antigo até expirar.
   */
  substituirToken(accessToken: string): void {
    localStorage.setItem(STORAGE_KEY, accessToken);
    this.accessToken.set(accessToken);
  }

  /** Token vigente, ou null. Faz logout automático se já expirou. */
  token(): string | null {
    const token = this.accessToken();
    if (token && tokenExpirado(token)) {
      this.logout();
      return null;
    }
    return token;
  }

  /**
   * Verifica se o token carrega a authority ('ESIC_RESPONDENTE' ou já
   * prefixada 'ROLE_ESIC_RESPONDENTE'). Checagem literal, apenas para
   * exibição de UI — a hierarquia de papéis (RoleHierarchy) e a decisão
   * final de acesso são sempre do backend.
   */
  hasRole(role: string): boolean {
    const roles = this.roles();
    return roles.includes(role) || roles.includes(`ROLE_${role}`);
  }

  /**
   * Como {@link hasRole}, mas considerando a hierarquia de papéis: um
   * ESIC_GESTOR satisfaz `hasRoleOuSuperior('ESIC_CADASTRADOR')`.
   *
   * É esta a checagem correta para decidir a exibição de ações na interface —
   * {@link hasRole} sozinho esconderia botões de quem tem um papel superior.
   * Continua valendo só para exibição: a autorização real é do backend.
   */
  hasRoleOuSuperior(role: string): boolean {
    if (this.hasRole(role)) {
      return true;
    }
    return Object.entries(HIERARQUIA_DE_PAPEIS).some(
      ([papel, contidos]) => contidos.includes(role) && this.hasRole(papel)
    );
  }

  /** Verdadeiro se o token carrega ao menos um dos papéis informados. */
  hasAlgumRole(roles: readonly string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }
}

function restaurarToken(): string | null {
  const token = localStorage.getItem(STORAGE_KEY);
  if (token && tokenExpirado(token)) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
  return token;
}

function tokenExpirado(token: string): boolean {
  const claims = decodificarClaims(token);
  return claims === null || claims.exp * 1000 <= Date.now() + CLOCK_SKEW_S * 1000;
}

/** Decodifica o payload do JWT (base64url) sem validar assinatura — isso é papel do backend. */
function decodificarClaims(token: string): UsuarioAutenticado | null {
  const partes = token.split('.');
  if (partes.length !== 3) {
    return null;
  }
  try {
    const base64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes));
    return {
      idUsuario: Number(payload.sub),
      email: payload.email,
      tenantId: payload.tenantId,
      pessoaId: payload.pessoaId ?? null,
      roles: payload.roles ?? [],
      exp: payload.exp
    };
  } catch {
    return null;
  }
}
